import { useCallback, useEffect, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { notificateError } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { useTossPaymentWidget } from '@shared/plugIn/tosspayments/useTossPaymentWidget';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { ANONYMOUS } from '@tosspayments/tosspayments-sdk';

/** 토스 결제 redirect 전후 폼 데이터 복원용 sessionStorage 키 */
export const TOSS_CHECKOUT_FORM_KEY = 'toss-checkout-form';

interface RequestPaymentParams {
  orderId: string;
  orderName: string;
  customerName: string;
  customerMobilePhone: string;
  /** sessionStorage 저장용 폼 데이터 */
  formData: object;
  shopItemId: string;
}

interface UseShopTossCheckoutParams {
  amount: number;
}

/**
 * 토스페이먼츠 결제 위젯 전용 훅
 * - 위젯 초기화 / 렌더링 / 금액 업데이트
 * - 모달 열기·닫기 상태
 * - requestPayment (redirect) 호출
 *
 * 주문 생성·삭제, guest 가입 등 비즈니스 로직은 페이지에서 처리한다.
 */
export const useShopTossCheckout = ({ amount }: UseShopTossCheckoutParams) => {
  const notificationInstance = useRecoilValue(notificationInstanceAtom);

  const { isReady, renderPaymentMethods, requestPayment, updateAmount } =
    useTossPaymentWidget({
      customerKey: ANONYMOUS,
      amount,
    });

  const paymentMethodsRenderedRef = useRef(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  //위젯 렌더링: 모달이 열리고 SDK 준비 완료 시 1회만
  useEffect(() => {
    if (isReady && isModalOpen && !paymentMethodsRenderedRef.current) {
      paymentMethodsRenderedRef.current = true;
      renderPaymentMethods(
        '#toss-payment-method',
        process.env.REACT_APP_TOSS_VARIANT_KEY,
      );
    }
  }, [isReady, isModalOpen, renderPaymentMethods]);

  // 금액 변경 시 위젯 금액 업데이트
  useEffect(() => {
    if (isReady && amount > 0) {
      updateAmount(amount);
    }
  }, [isReady, amount, updateAmount]);

  //모달 열기 / 닫기
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setIsSubmitting(false);
  }, []);

  //결제 요청 (sessionStorage 저장 → requestPayment redirect)
  const requestModalPayment = useCallback(
    async (params: RequestPaymentParams) => {
      if (isSubmitting || !isReady) return;

      try {
        setIsSubmitting(true);

        // 폼 데이터 저장 (redirect 복귀 시 복원용)
        sessionStorage.setItem(
          TOSS_CHECKOUT_FORM_KEY,
          JSON.stringify({ itemId: params.shopItemId, data: params.formData }),
        );

        const baseUrl = window.location.origin + window.location.pathname;
        const bugoParam = new URLSearchParams(window.location.search).get('bugoId');
        const bugoQuery = bugoParam ? `&bugoId=${bugoParam}` : '';

        await requestPayment({
          orderId: params.orderId,
          orderName: params.orderName,
          successUrl: `${baseUrl}?result=success${bugoQuery}`,
          failUrl: `${baseUrl}?result=fail${bugoQuery}`,
          customerName: params.customerName,
          customerMobilePhone: params.customerMobilePhone,
        });
      } catch (err) {
        setIsSubmitting(false);
        const error = err as { code?: string; message?: string };

        // 사용자가 토스 결제창 닫기 → 모달 유지
        if (error.code === 'USER_CANCEL') {
          return;
        }

        notificateError(notificationInstance, `결제 요청 실패\n${error.message || ''}`);
      }
    },
    [isSubmitting, isReady, requestPayment, notificationInstance],
  );

  return {
    isReady,
    isModalOpen,
    isSubmitting,
    openModal,
    closeModal,
    requestModalPayment,
    updateAmount,
  };
};
